// React and hooks
import React, { useCallback, useMemo, useState } from 'react';

// Third-party libraries
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faFileCheck, faFileExclamation, faTrash } from '@fortawesome/pro-solid-svg-icons';
import cx from 'classnames';
import * as yup from 'yup';
import Bugsnag from '@bugsnag/js';

// Formik
import { Form, Formik } from 'formik';

// UI Components
import Alert from '../Alert/Alert';
import Button from '../Button/Button';
import FormikInput from '../Input/Formik/Input';
import Link, { LinkIcons } from '../Link/Link';
import Modal from '../Modal/Modal';

// Types and utilities
import { CustomFile } from '../../types/CompanyAnswersTypes';
import { getTableDate } from '../../services/utils';

const validationSchema = yup.object().shape({
    files: yup.array().of(
        yup.object().shape({
            customName: yup.string().max(50, 'Nafn má ekki vera lengra en 50 stafir').required('Skjal verður að hafa nafn')
        })
    )
});
// Define the props interface for the component
interface FileUploadDropzoneProps {
    answerListId: number; // The ID of the answer list to associate uploaded files with
    initialFiles: CustomFile[]; // Array of files that have already been uploaded
    onUploadSuccess?: (uploadedFiles: any) => void; // Optional callback for successful uploads
}

// Define the structure for our internal file tracking
interface FileWithCustomName {
    file: File; // The actual File object
    customName: string; // User-editable name for the file
}

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({ answerListId, initialFiles }) => {
    // State to store the uploaded files with their custom names
    const [files, setFiles] = useState<FileWithCustomName[]>([]);
    const [uploadedFiles, setUploadedFiles] = useState<CustomFile[]>(initialFiles);

    // State to track upload progress and status
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<number | undefined>(undefined);
    const [uploadError, setUploadError] = useState<string>('');

    // State to store rejection reason during drag
    const [dragRejectionReason, setDragRejectionReason] = useState<string>('');

    //state and function for handling file deletion
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [fileToDelete, setFileToDelete] = useState<CustomFile | null>(null);

    // Accepted file types that match your requirements
    // Comprehensive mapping of MIME types to file extensions
    // Multiple MIME types per file format ensure compatibility across different browsers,
    // operating systems, and older applications that may report non-standard MIME types
    const acceptedFileTypes: Record<string, string[]> = {
        // PDF files
        'application/pdf': ['.pdf'],
        'application/x-pdf': ['.pdf'], // Legacy/non-standard MIME type used by some older systems

        // Word DOC files (legacy format)
        'application/msword': ['.doc'],

        // Word DOCX files (modern format)
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],

        // Excel XLS files (legacy format)
        'application/vnd.ms-excel': ['.xls'],
        'application/msexcel': ['.xls'], // Alternative MIME type
        'application/x-msexcel': ['.xls'], // Another alternative used by some systems

        // Excel XLSX files (modern format)
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],

        // JPEG image files
        'image/jpeg': ['.jpg', '.jpeg'],
        'image/jpg': ['.jpg', '.jpeg'], // Some systems report this instead of image/jpeg

        // PNG image files
        'image/png': ['.png'],

        // CSV files - has multiple MIME types depending on the system
        'text/csv': ['.csv'],
        'text/comma-separated-values': ['.csv'], // Alternative MIME type
        'application/csv': ['.csv'], // Sometimes reported by certain applications

        // ZIP files - particularly problematic across different systems
        'application/zip': ['.zip'],
        'application/x-zip-compressed': ['.zip'], // Windows/Internet Explorer
        'application/x-compressed': ['.zip'], // Another Windows variant
        'multipart/x-zip': ['.zip'], // Legacy MIME type
        'application/x-zip': ['.zip'] // Yet another variant seen in the wild
    };

    // Helper function to check if a file type is accepted
    const isFileTypeAccepted = (fileType: string): boolean => {
        // Check if the file's MIME type matches any of our accepted types
        return Object.keys(acceptedFileTypes).includes(fileType);
    };

    // Custom validation function that runs during drag enter
    const validateDraggedFiles = (event: React.DragEvent<HTMLElement>) => {
        // Access the files being dragged from the dataTransfer object
        const items = event.dataTransfer.items;

        // If no items are being dragged, return
        if (!items || items.length === 0) {
            setDragRejectionReason('');
            return;
        }

        // Check each dragged item
        for (let i = 0; i < items.length; i++) {
            const item = items[i];

            // Only validate file items (not directories or other types)
            if (item.kind === 'file') {
                const fileType = item.type;


                // Check if the file type is in our accepted list
                if (!isFileTypeAccepted(fileType)) {
                    // Set rejection reason - file type not accepted
                    setDragRejectionReason(`Skjal af tegund "${fileType || 'óþekkt'}" er ekki samþykkt`);
                    return;
                }
            }
        }

        // If we get here, all files pass validation
        setDragRejectionReason('');
    };

    // Callback function that handles when files are dropped or selected
    const onDrop = useCallback((acceptedFiles: File[]) => {
        // Transform the accepted files into our internal format with custom names
        // By default, the custom name is the original file name (without extension)
        const newFiles = acceptedFiles.map(file => ({
            file,
            customName: file.name.substring(0, file.name.lastIndexOf('.')) || file.name
        }));

        // Add the newly uploaded files to the existing files array
        setFiles(prevFiles => [...prevFiles, ...newFiles]);

        // Clear any drag rejection reason once files are dropped
        setDragRejectionReason('');

        // Clear any previous upload errors
        setUploadError('');
    }, []);

    // Configure react-dropzone with our settings
    const { getRootProps, getInputProps, isDragActive, isDragReject, isDragAccept } = useDropzone({
        onDrop,
        accept: acceptedFileTypes,
        multiple: true, // Allow multiple file uploads
        onDragEnter: validateDraggedFiles // Add our custom validation on drag enter
    });

    // Function to remove a file from the list
    const removeFile = (indexToRemove: number) => {
        setFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
    };

    const handleDeleteFile = async (fileId: number) => {
        setIsDeleting(fileId);
        try {
            const response = await fetch(`/DueDiligence/DeleteFile/${fileId}`, {
                method: 'DELETE'
            });
            if (!response.ok) {
                throw new Error('Failed to delete file');
            }
            setUploadedFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
        } catch (error) {
            console.error('Error deleting file:', error);
            return false;
        } finally {
            setIsDeleting(undefined);
        }
    }

    // Function to handle the form submission and upload files to the server
    const handleUploadSubmit = async (values: { files: { customName: string }[] }) => {
        // Prevent submission if already uploading
        if (isUploading) return;

        setIsUploading(true);
        setUploadError('');
        if (files.length + uploadedFiles.length > 10) {
            setUploadError('Fjöldi skjala (10) hefur verið náð');
            setIsUploading(false);
            return;
        }

        try {
            // Create a new FormData object to send files as multipart/form-data
            const formData = new FormData();

            // Add the AnswerListId to the form data
            formData.append('AnswerListId', answerListId.toString());

            // Loop through each file and add it to the form data with the required structure
            files.forEach((fileItem, index) => {
                // Get the custom name from the form values (in case user edited it)
                const customName = values.files[index].customName;

                // Add the original file name
                // Backend expects: Documents[0].OriginalName, Documents[1].OriginalName, etc.
                formData.append(`Documents[${index}].OriginalName`, fileItem.file.name);

                // Add the user's selected/custom name for the file
                // Backend expects: Documents[0].SelectedName, Documents[1].SelectedName, etc.
                formData.append(`Documents[${index}].SelectedName`, customName);

                // Add the actual file blob
                // Backend expects: Documents[0].File, Documents[1].File, etc.
                formData.append(`Documents[${index}].File`, fileItem.file);
            });

            // Make the API call to upload the files
            const response = await fetch('/DueDiligence/UploadKycDocuments', {
                method: 'POST',
                body: formData,
                // Note: Do NOT set Content-Type header manually when sending FormData
                // The browser will set it automatically with the correct boundary parameter
            });

            // Check if the response was successful
            if (!response.ok) {
                // Try to parse error message from response
                const errorData = await response.text().catch(() => null);
                throw new Error(errorData || `Upload failed with status: ${response.status}`);
            }

            // Parse the successful response
            const result = await response.json();

            // Update the uploaded files state with the new files from the server
            result.forEach((uploadedFile: CustomFile) => {
                setUploadedFiles(prevFiles => [...prevFiles, uploadedFile]);
            });
            setFiles([]); // Clear the files after successful upload

        } catch (error) {
            // Handle any errors that occurred during upload

            if (error instanceof Error) {
                Bugsnag.notify(error);
                if (error.message.includes("Amazon.S3.AmazonS3Exception")) {
                    setUploadError('Ekki tókst að vista skrá.');
                } else {
                    setUploadError(error.message.slice(0, 100));
                }
            } else {
                setUploadError('Eitthvað fór úrskeiðis við að vista skrá. Reyndu aftur.');
            }
        } finally {
            // Reset uploading state
            setIsUploading(false);
        }
    };

    // Function to update a file's custom name when user edits it
    const updateFileName = (index: number, newName: string) => {
        setFiles(prevFiles => {
            const updatedFiles = [...prevFiles];
            updatedFiles[index] = {
                ...updatedFiles[index],
                customName: newName
            };
            return updatedFiles;
        });
    };

    const disableButtons = useMemo(() => isUploading || isDeleting !== undefined, [isUploading, isDeleting]);

    return (
        <>
            <div className="file-upload-container">
                {/* Dropzone area */}
                <div
                    {...getRootProps()}
                    className={cx(`dropzone`, {
                        'dropzone--accept': isDragAccept && isDragActive,
                        'dropzone--reject': isDragReject && isDragActive
                    })}
                >
                    <input {...getInputProps()} />
                    {/* Show rejection reason during drag if file will be rejected */}
                    {(isDragReject && dragRejectionReason) ? (
                        <>
                            <div className="dropzone__icon-wrapper">
                                <div className="dropzone__icon-circle">
                                    <FontAwesomeIcon icon={faFileExclamation} className="dropzone__icon" />
                                </div>
                            </div>
                            <p className="dropzone__error">
                                {dragRejectionReason}
                            </p>
                        </>
                    ) : <>
                        {/* Upload icon */}
                        <div className="dropzone__icon-wrapper">
                            <FontAwesomeIcon icon={isDragAccept && isDragActive ? faFileCheck : faArrowUpFromBracket} className="dropzone__icon" />
                        </div>
                        <div className='dropzone__text'>
                            {/* Main heading */}
                            <h3 className="dropzone__title">
                                Smelltu hér eða dragðu
                            </h3>

                            {/* Supported file types */}
                            <p className="dropzone__file-types">
                                PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, CSV OG ZIP
                            </p>

                            {/* Additional instruction */}
                            <p className="dropzone__instruction">
                                skjölin þín á þetta svæði.
                            </p>
                        </div>
                    </>}
                </div>

                <p className="paragraph-small--bold file-upload-container__info">
                    Skjölin þín birtast hér fyrir neðan, þú getur breytt heiti skjalanna eða eytt þeim. Athugið að hámarks fjöldi stafa er 50 stafir.
                </p>

                {
                    uploadedFiles.length > 0 && (
                        <div className="uploaded-file-list">
                            {uploadedFiles.map((file, index) => (
                                <div className='uploaded-file-list__item' key={index}>
                                    <div className='uploaded-file-list__item--left-section'>
                                        <p className='file-item__index'>{index + 1}.</p>
                                        <p className='paragraph-small'>{getTableDate(file.uploadTimestamp, "DD/MM/YYYY HH:MM:SS", ".")}</p>
                                        <Link disabled={isDeleting === file.id} url={`/DueDiligence/GetFile/${file.id}`} icon={getFileIcon(file.fileExtensionType)} className='uploaded-file-list__name'>
                                            {file.encodedFileName}
                                        </Link>
                                    </div>
                                    <div>
                                        <Button showLoader={isDeleting === file.id} disabled={disableButtons} onClick={() => {
                                            setFileToDelete(file);
                                            setShowDeleteModal(true);
                                        }} icon={faTrash} buttonType='secondary' size='lg' />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                }

                {/* Display uploaded files */}
                {files.length > 0 && (
                    <div className="file-list">
                        <div className="file-list__items">
                            <Formik
                                validationSchema={validationSchema}
                                // Initialize form values with the custom names from our files state
                                initialValues={{
                                    files: files.map(f => ({ customName: f.customName }))
                                }}
                                // Enable reinitialization so form updates when files change
                                enableReinitialize
                                onSubmit={handleUploadSubmit}
                            >
                                {({ values, setFieldValue }) => (
                                    <Form>
                                        {files.map((fileItem, index) => {
                                            return (
                                                <div key={index} className="file-item">
                                                    <p className='file-item__index'>{uploadedFiles.length + index + 1}.</p>
                                                    <div className='file-item__name-and-inputLength'>
                                                        <FormikInput
                                                            disabled={disableButtons}
                                                            absoluteError
                                                            fullWidth
                                                            name={`files[${index}].customName`}
                                                            placeholder="File name"
                                                            // Use the value from Formik's values
                                                            value={values.files[index]?.customName || ''}
                                                            // Update both Formik state and our local state when name changes
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                                                                const newValue = e.target.value;
                                                                setFieldValue(`files[${index}].customName`, newValue);
                                                                updateFileName(index, newValue);
                                                            }}
                                                            // Display the file type badge
                                                            fileType={
                                                                acceptedFileTypes[fileItem.file.type as keyof typeof acceptedFileTypes] &&
                                                                acceptedFileTypes[fileItem.file.type as keyof typeof acceptedFileTypes][0]
                                                                    .replace('.', '')
                                                                    .toUpperCase()
                                                            }
                                                            maxLength={50}
                                                        />
                                                        <span className='file-item__name-and-inputLength__Length'>
                                                            {values.files[index]?.customName.length || 0}/50
                                                        </span>
                                                    </div>
                                                    <Button
                                                        icon={faTrash}
                                                        disabled={disableButtons}
                                                        buttonType='secondary'
                                                        size='lg'
                                                        onClick={() => removeFile(index)}
                                                        className="file-item__remove"
                                                        aria-label="Remove file"
                                                        type="button" // Prevent form submission when clicking remove
                                                    />
                                                </div>
                                            )
                                        })}
                                        <Button
                                            type='submit'
                                            showLoader={isUploading}
                                            //disabled if total items (uploaded + to be uploaded) is 10
                                            disabled={disableButtons}
                                        >
                                            Vista skrár
                                        </Button>
                                        {/* Display any upload errors */}
                                        {uploadError && (
                                            <div className="upload-error">
                                                <Alert type={'error'} headText={"Villa"} text={uploadError} />
                                            </div>
                                        )}
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </div>
                )}
                <AreYouSureYouWantToDeleteFile
                    file={fileToDelete || null}
                    onDelete={handleDeleteFile} show={showDeleteModal}
                    onHide={() => setShowDeleteModal(false)} />
            </div>
        </>

    );
};

export default FileUploadDropzone;

const AreYouSureYouWantToDeleteFile: React.FC<{
    file: CustomFile | null,
    onDelete: (id: number) => void,
    show: boolean,
    onHide: () => void
}> = ({ file, onDelete, show, onHide }) => {
    if (!file) return null;
    else {
        return <Modal
            hasCloseButton
            header="Eyða skjali"
            show={show}
            body={
                <>
                    <p>Ertu viss um að þú viljir eyða skjalinu <Link url={`/DueDiligence/GetFile/${file.id}`} icon={getFileIcon(file.fileExtensionType)} className='uploaded-file-list__name'>
                        {file.encodedFileName}
                    </Link> ?</p>
                    <div className='modal__button-row' >
                        <Button style={{ marginRight: "20px" }} buttonType='secondary' onClick={onHide}>Hætta við</Button>
                        <Button buttonType='primary' onClick={() => { onDelete(file.id); onHide(); }}>Eyða skjali</Button>
                    </div>
                </>
            }
            onHide={onHide}
        />
    }
}

/**
 * Returns the appropriate icon key based on the file extension
 * @param fileExtension - The file extension (with or without the leading dot)
 * @returns The icon key that corresponds to an entry in iconMap
 */
export const getFileIcon = (fileExtension: string): LinkIcons => {
    // Remove leading dot if present and convert to lowercase for comparison
    const extension = fileExtension.toLowerCase().replace(/^\./, '');

    switch (extension) {
        case 'pdf':
            return 'pdf';
        case 'doc':
        case 'docx':
            return 'word';
        case 'xls':
        case 'xlsx':
            return 'excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
            return 'image';
        case 'csv':
            return 'csv';
        case 'zip':
            return 'zip';
        default:
            return 'download';
    }
};